import { Box, FormField, FormGrid } from '@dixa/lib-shared-habla';
import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { customFieldsApi } from 'apis/customFields';
import {
  elevionArrayComparators,
  ElevionFieldType,
  ElevionLeafFieldID,
  ElevionLeafNode,
  elevionStringComparators,
} from 'utils/elevion';

import { AttributeFormFieldValues } from '../../../../../customAttributeModalForm.types';

import {
  fieldTypeComparatorMap,
  LeafComparatorField,
} from './component/leafComparatorField/leafComparatorField';
import { LeafFieldField } from './component/leafFieldField/leafFieldField';
import { LeafValueField } from './component/leafValueField/leafValueField';

export type ElevionLeafConditionProps = {
  parentName?: string;
  index?: number;
};

export const ElevionConditionLeaf = memo(function ElevionLeafCondition({
  index,
  parentName,
}: ElevionLeafConditionProps): JSX.Element {
  // TODO: [TEAMS-13693] remove omit after updating to RHF v8
  const { control, setValue } =
    useFormContext<Omit<AttributeFormFieldValues, 'options'>>();

  const name =
    parentName !== undefined && index !== undefined
      ? (`${parentName}.${index}` as 'visibility_condition')
      : 'visibility_condition';
  const node = useWatch({
    control,
    name,
  }) as ElevionLeafNode;

  const response = customFieldsApi.useReadAttributesQuery({
    entityId: 'conversation',
  });
  const customFields = response.data?.data?.attributes ?? [];
  const activeCustomFields = customFields.filter(
    (field) => !field.is_deactivated,
  );
  const previousNode = usePrevious(node);

  const getExpectedElevionFieldType = useCallback((): ElevionFieldType => {
    if (node.field === ElevionLeafFieldID.Queue) {
      return 'safield';
    }

    const identifier = node.field.split('custom__')[1];
    const selectedField = activeCustomFields.find(
      (field) => field.identifier === identifier,
    );

    return selectedField?.type === 'select' ? 'safield' : 'sfield';
  }, [activeCustomFields, node.field]);

  useEffect(() => {
    const expectedElevionFieldType = getExpectedElevionFieldType();
    const isArrayType = expectedElevionFieldType === 'safield';
    const isStringType = expectedElevionFieldType === 'sfield';
    const isSupportedComparator = fieldTypeComparatorMap[
      expectedElevionFieldType
    ].includes(node.comparator as never);
    const compatibleComparator = isSupportedComparator
      ? node.comparator
      : isArrayType
      ? elevionArrayComparators[0]
      : elevionStringComparators[0];

    if (node.field_type !== expectedElevionFieldType) {
      setValue(`${name}.field_type`, expectedElevionFieldType);
    }

    if (previousNode !== undefined) {
      if (previousNode.field !== node.field) {
        if (!isSupportedComparator) {
          setValue(`${name}.comparator`, compatibleComparator);
        }

        if (isArrayType) {
          setValue(`${name}.value`, []);
        } else if (isStringType) {
          setValue(`${name}.value`, '');
        }
      } else if (previousNode.comparator !== node.comparator) {
        if (node.comparator === 'is_empty' && node.value.length > 0) {
          setValue(`${name}.value`, []);
        }
      }
    }
  }, [getExpectedElevionFieldType, name, node, previousNode, setValue]);

  return (
    <Box margin="0 0 0 200" width="100%">
      <FormGrid>
        <FormField span={2}>
          <LeafFieldField parentName={name} customFields={activeCustomFields} />
        </FormField>
        <FormField span={2}>
          <LeafComparatorField parentName={name} />
        </FormField>
        <FormField span={2}>
          <LeafValueField parentName={name} customFields={activeCustomFields} />
        </FormField>
      </FormGrid>
    </Box>
  );
});
